/**
 * 顧客契約 CSV アップロードの frontend 前段チェック
 * (spec `docs/specs/admin/data/uploads/customer-contract.md` §2 列フォーマット)。
 *
 * このモジュールは **行の中身をパースしない**。CSV は生バイトのまま ImportCustomerContracts RPC に
 * 送り、厳格モード (ヘッダー完全一致・列数・値域) と差分判定は backend が行う。ここで見るのは
 * common.md Step1 の「クライアント側構文チェック」に対応する軽い前段だけ — 文字コード / ヘッダー行 /
 * データ行の有無 (ファイルサイズは読み込み前に呼び出し側が見る)。サーバー側検証を置き換えるものではない。
 */
import { CUSTOMER_CONTRACT_CSV_ACCEPTED_HEADERS } from '~/models/customerContractCsvHeader'

export type PrecheckErrorKey =
  | 'fileSizeExceeded'
  | 'readFailed'
  | 'notUtf8'
  | 'headerMismatch'
  | 'noDataRows'

export type PrecheckResult =
  | { ok: true }
  | {
      ok: false
      messageKey: PrecheckErrorKey
      params?: Record<string, number | string>
    }

/** UTF-8 BOM。Excel が既定で付けるため許容する (common.md)。 */
const UTF8_BOM = '\uFEFF'

/** エラー文へ載せる実際ヘッダーの表示上限。 */
const MAX_HEADER_ECHO = 200

const truncateHeader = (s: string): string => {
  const trimmed = s.trim()
  return trimmed.length <= MAX_HEADER_ECHO
    ? trimmed
    : `${trimmed.slice(0, MAX_HEADER_ECHO)}…`
}

/**
 * ヘッダー行をサーバーと同じ緩さに正規化してから比較する。backend は csv.Reader で unquote した上で
 * セル単位に TrimSpace する。前段がそれより厳しいと、サーバーなら通る CSV を Step1 で弾いてしまう。
 */
const normalizeHeader = (line: string): string =>
  line
    .split(',')
    .map((cell) =>
      cell
        .trim()
        .replace(/^"(.*)"$/, '$1')
        .trim(),
    )
    .join(',')

/**
 * Step1 の前段チェック。行数上限はここでは見ない (引用符内の改行は物理行数より論理レコードを
 * 少なくするため、前段で弾くとサーバーなら通る CSV を拒否しうる)。データ行が 0 件だけは
 * 物理行 0 → 論理レコード 0 が常に成り立つので前段で見る。
 */
export const precheckCustomerContractCsv = (text: string): PrecheckResult => {
  const body = text.startsWith(UTF8_BOM) ? text.slice(UTF8_BOM.length) : text
  const lines = body.split(/\r?\n/)
  const rawHeader = lines[0] ?? ''
  if (
    !CUSTOMER_CONTRACT_CSV_ACCEPTED_HEADERS.includes(normalizeHeader(rawHeader))
  ) {
    // AC-DUC-003: 期待ヘッダーだけでなく実際のヘッダーも見せる
    return {
      ok: false,
      messageKey: 'headerMismatch',
      params: { actual: truncateHeader(rawHeader) },
    }
  }
  if (!lines.slice(1).some((l) => l.trim() !== '')) {
    return { ok: false, messageKey: 'noDataRows' }
  }
  return { ok: true }
}
